<template>
    <div class="vx-auto-suggest" v-click-outside="setIsOutside">
        <div class="flex w-full items-center relative">
            <!-- Input -->
            <vx-input
                ref="input"
                v-bind="$attrs"
                v-on="listeners"
                v-model="searchQuery"
                :disabled="disabled"
                :label="label"
                :clearable="clearable && selectLike && searchQuery && !disabled"
                :placeholder="placeholder"
                :label-placeholder="labelPlaceholder"
                :remove-validation-padding="removeValidationPadding"
                :class="[customClasses, { active: focused }]"
                :icon="parsedIcon"
                class="z-50"
                icon-pack="feather"
                icon-no-border
                select-on-focus
                disable-chrome-autocomplete
                autocomplete="nope"
                @keyup.esc="close"
                @keydown.up="increaseIndex(false)"
                @keydown.down="increaseIndex"
                @keydown.enter.prevent="suggestionSelected"
            />
        </div>

        <!-- Group List -->
        <ul
            v-if="!hideList"
            ref="scrollContainer"
            :class="{
                'hidden': hideList,
                'negate-validation-padding': !removeValidationPadding || $attrs['danger-text']
            }"
            class="auto-suggest-suggestions-list z-50 rounded-lg mt-2 shadow-lg overflow-x-hidden"
            @mouseenter="insideSuggestions = true"
            @mouseleave="insideSuggestions = false"
            @wheel.stop
            tabindex="-1"
        >
            <li
                ref="grp_list"
                v-for="(suggestions, grp_name, grp_index) in filteredData"
                :key="grp_index"
                class="auto-suggest__suggestion-group-container"
            >
                <!-- Group Header -->
                <p
                    class="auto-suggest__suggestion-group-title pt-3 pb-1 px-4"
                    v-if="!hideGroupTitle"
                >
                    <slot name="group" :group_name="grp_name"></slot>
                </p>

                <!-- Suggestion List of each group -->
                <ul>
                    <template v-for="(suggestion, index) in suggestions.list">
                        <li
                            v-if="index <= renderLimit"
                            :key="index"
                            :ref="`${grp_index}.${index}`"
                            class="auto-suggest__suggestion-group__suggestion py-3 px-4 cursor-pointer"
                            :class="{
                                'vx-auto-suggest__current-selected':
                                    currentSelected == `${grp_index}.${index}`
                            }"
                            @mouseenter="currentSelected = `${grp_index}.${index}`"
                            @click="suggestionSelected"
                        >
                            <vx-scroll-trigger
                                v-if="
                                    shouldTryRaiseRenderLimit(index) || shouldTriggerLoadMore(index)
                                "
                                :parent="$refs.scrollContainer"
                                @visible="tryLoadMore(grp_name, index)"
                            >
                                <slot :name="grp_name" :suggestion="suggestion"></slot>
                            </vx-scroll-trigger>
                            <slot v-else :name="grp_name" :suggestion="suggestion"></slot>
                        </li>
                    </template>

                    <li
                        v-if="suggestions.loading"
                        class="auto-suggest__suggestion-group__suggestion pb-2 px-4"
                    >
                        <slot name="loader">
                            <vx-spinner :scale="0.6"></vx-spinner>
                        </slot>
                    </li>
                    <li
                        class="auto-suggest__suggestion-group__suggestion no-results"
                        v-else-if="!suggestions.list.length && searchQuery"
                    >
                        <slot name="noResult" :group_name="grp_name">
                            <p class="py-3 px-4">No Results Found.</p>
                        </slot>
                    </li>
                </ul>
            </li>
        </ul>
    </div>
</template>

<script>
import VxScrollTrigger from './VxScrollTrigger'
import ClickOutside from 'vue-click-outside'

export default {
    props: {
        placeholder: {
            type: String
        },
        data: {
            type: Object,
            required: true
        },
        inputClassses: {
            type: [String, Object, Array]
        },
        value: {
            type: String,
            default: ''
        },
        removeValidationPadding: {
            type: Boolean,
            default: false
        },
        icon: {
            type: String,
            default: 'icon-search'
        },
        autoFocus: {
            type: Boolean,
            default: false
        },
        showOnFocus: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        searchLimit: {
            type: Number,
            default: 0
        },
        hideGroupTitle: {
            type: Boolean,
            default: false
        },
        selectLike: {
            type: Boolean,
            default: true
        },
        label: {
            type: String
        },
        selected: {
            type: Object
        },
        labelPlaceholder: {
            type: String
        },
        preventFiltering: {
            type: Boolean
        },
        clearable: {
            type: Boolean,
            default: true
        }
    },
    components: {
        VxScrollTrigger
    },
    directives: {
        ClickOutside
    },
    data() {
        return {
            currentSelected_x: -1,
            insideSuggestions: false,
            hideList: true,
            focused: false,
            backup_selected: null,
            currentRenderedChunk: 0,
            renderChunkSize: 20
        }
    },
    watch: {
        autoFocus(val) {
            if (val) this.focus()
            else this.searchQuery = ''
        },
        filteredData(val) {
            // Auto Select first item if it's not item-404
            let grp_index = null

            if (!this.hasItems) {
                this.currentSelected = -1
            } else if (this.currentSelected == -1) {
                for (let [index, grp_suggestions] of Object.values(val).entries()) {
                    if (grp_suggestions.list.length) {
                        grp_index = index
                        break
                    }
                }

                this.currentSelected = grp_index + '.0'
            }
        },
        selected: {
            handler: function (newVal) {
                var newText = ''
                if (newVal && this.selectLike && Object.keys(this.data).length == 1) {
                    var grp = Object.values(this.data)[0]
                    newText = this.getText(newVal, grp)
                }
                this.searchQuery = newText
            },
            immediate: true
        }
    },
    computed: {
        attrs() {
            return { ...this.$attrs, selected: null }
        },
        listeners() {
            return {
                ...this.$listeners,
                input: () => {},
                focus: this.onInputFocus,
                blur: this.onInputBlur
            }
        },
        renderLimit() {
            return (this.currentRenderedChunk + 1) * this.renderChunkSize
        },
        customClasses() {
            return this.inputClassses
        },
        parsedIcon() {
            if (this.icon) {
                var kebabIcon = this.icon.toKebabCase()
                if (kebabIcon.endsWith('-icon')) {
                    var iconName = kebabIcon.substring(0, kebabIcon.length - 5)
                    return `icon-${iconName}`
                }
            }
            return this.icon || 'icon-search'
        },
        searchQuery: {
            get() {
                return this.value
            },
            set(term) {
                if (this.value === term) {
                    return
                }
                if (term && this.focused) {
                    this.hideList = false
                }
                this.$emit('input', term)
            }
        },
        selectedItem: {
            get() {
                return this.selected
            },
            set(item) {
                if (!this.selected || item.id != this.selected.id) {
                    this.$emit('update:selected', item)
                    this.currentSelected = '0.0'
                    this.searchQuery = item.text
                }

                this.$nextTick(() => {
                    // doing this in next tick due to the searchQuery setter
                    // triggering during the selection process
                    this.close()
                    this.$emit('blur')
                })
            }
        },
        filteredData() {
            let queried_data = {}
            const data_grps = Object.keys(this.data)

            data_grps.forEach((grp, i) => {
                var groupMeta = this.data[grp]
                queried_data[grp] = {
                    list: this.filter_grp(groupMeta),
                    loading: !!this.data[grp].loading
                }
            })
            return queried_data
        },
        shouldLazyLoad() {
            return this.filteredData.length > 30
        },
        triggerIndex() {
            var grp_names = Object.keys(this.data)
            if (grp_names.length == 1) {
                var filtered_grp = this.filteredData[grp_names[0]].list
                var halfWay = Math.floor(filtered_grp.length / 2)
                var nearEnd = filtered_grp.length - 10

                return Math.max(halfWay, nearEnd)
            }
            return -1
        },
        hasItems() {
            return Object.values(this.filteredData).some((grp) => grp.list.length)
        },
        lastIndexString() {
            var entries = Object.entries(this.filteredData)
            var lastGrp = entries.length - 1
            var lastItem = entries[lastGrp][1].list.length - 1
            return `${lastGrp}.${lastItem}`
        },
        getId() {
            return function (item, grp) {
                if (typeof grp.id === 'string') {
                    return item[grp.id]
                } else {
                    return grp.id(item)
                }
            }
        },
        getText() {
            return function (item, grp) {
                if (typeof grp.getSearchText === 'string') {
                    return item[grp.getSearchText]
                } else {
                    return grp.getSearchText(item)
                }
            }
        },
        currentSelected: {
            get() {
                return this.currentSelected_x
            },
            set(v) {
                if (v == -1) {
                    this.currentSelected_x = v
                    return
                }

                var selectedObject = null
                if (v != null) {
                    const [grp_i, item_i] = v.split('.')
                    var grpKey = Object.keys(this.data)[grp_i]
                    selectedObject = this.data[grpKey].data[item_i]
                }
                this.$emit('hovered', selectedObject)
                this.currentSelected_x = v
            }
        }
    },
    methods: {
        setIsOutside() {
            this.insideSuggestions = false
        },
        tryLoadMore(grp_name, index) {
            if (this.shouldTriggerLoadMore(index)) {
                this.$emit('load-more', grp_name)
            }
            if (index == this.renderLimit - this.renderChunkSize / 2) {
                this.currentRenderedChunk++
            }
        },
        clearSelection() {
            this.searchQuery = ''
        },
        shouldTryRaiseRenderLimit(index) {
            var numResults = this.filteredData[Object.keys(this.data)[0]].list.length
            // at the end of the list
            if (index === numResults - 1) {
                return true
            }
            return index % this.renderChunkSize === this.renderChunkSize / 2
        },
        shouldTriggerLoadMore(index) {
            if (this.triggerIndex >= 5) {
                var numResults = this.filteredData[Object.keys(this.data)[0]].list.length
                // at the end of the list
                if (index === numResults - 1) {
                    return true
                }

                if (index == this.triggerIndex) {
                    return true
                }
            }
            return false
        },
        close() {
            if (!this.hideList) {
                this.$emit('closeSearchbar')
            }
            this.hideList = true
            if (!this.selectLike) {
                this.searchQuery = ''
            } else if (!this.selected && this.backup_selected) {
                // Todo: set the backed up value back to selected on blur when nothing else is set
                // this.$emit('update:selected', this.backup_selected)
            }
        },
        filter_grp(grp) {
            var exact = []
            var wordstart = []
            var contains = []

            if (this.searchQuery) {
                var search_term = this.searchQuery.toLowerCase()
                grp.data.forEach((item) => {
                    var text = this.getText(item, grp)

                    if (text) {
                        var textLower = text.toLowerCase()
                        var index = textLower.indexOf(search_term)

                        if (index != -1) {
                            // retain the sort order if coming from server
                            if (index == 0 || grp.isPreSorted) {
                                exact.push(item)
                            } else if (/\s/.test(textLower[index - 1])) {
                                wordstart.push(item)
                            } else {
                                contains.push(item)
                            }
                        }
                    }
                })
            } else {
                exact = grp.data
            }

            var full_list = exact.concat(wordstart, contains)

            if (this.searchLimit > 0) {
                return full_list.slice(0, this.searchLimit)
            }
            return full_list
        },
        onInputFocus(e) {
            this.focused = true
            this.backup_selected = this.selected
            this.clearSelection()
            if (this.showOnFocus) {
                this.hideList = false
            }
            this.$emit('focus', e)
        },
        onInputBlur(e) {
            if (this.insideSuggestions) return
            this.focused = false
            this.$emit('blur', e)
            this.close()
        },
        suggestionSelected() {
            if (this.currentSelected > -1) {
                const [grp_index, item_index] = this.currentSelected.split('.')
                const groupName = Object.keys(this.data)[grp_index]
                const item = this.filteredData[groupName].list[item_index]
                const grp = this.data[groupName]
                const text = this.getText(item, grp)
                const id = this.getId(item, grp)

                this.selectedItem = {
                    id,
                    item,
                    groupName,
                    text
                }
            }
        },
        increaseIndex(val = true) {
            // If there's no matching items
            if (!this.hasItems) return

            if (this.currentSelected == -1) {
                return
            }

            const [grp_i, item_i] = this.currentSelected.split('.')
            const grp_arr = Object.entries(this.filteredData)
            const active_grp_total_items = grp_arr[grp_i][1].list.length

            if (val) {
                this.hideList = false
                // If active item is not of last item in grp
                if (active_grp_total_items - 1 > item_i) {
                    this.currentSelected = grp_i + '.' + (Number(item_i) + 1)

                    // If active item grp is not last in grp list
                } else if (grp_i < grp_arr.length - 1) {
                    for (let i = Number(grp_i) + 1; i < grp_arr.length; i++) {
                        // If navigating group have items => Then move in that group
                        if (grp_arr[i][1].length > 0) {
                            this.currentSelected = Number(i) + '.0'
                            break
                        }
                    }
                }
            } else {
                // If active item is not of first item in grp
                if (Number(item_i)) {
                    this.currentSelected = grp_i + '.' + (Number(item_i) - 1)

                    // If active item grp  is not first in grp list
                } else if (Number(grp_i)) {
                    for (let i = Number(grp_i) - 1; i >= 0; i--) {
                        // If navigating group have items => Then move in that group
                        if (grp_arr[i][1].list.length > 0) {
                            this.currentSelected = i + '.' + (grp_arr[i][1].list.length - 1)
                            break
                        }
                    }
                }
            }
            this.scrollToSelection()
        },
        scrollToSelection() {
            var element = this.$refs[this.currentSelected][0]
            var scroll = this.$refs.scrollContainer

            if (this.currentSelected == this.lastIndexString) {
                scroll.scrollTop = scroll.scrollHeight
            } else if (
                element.offsetTop + element.offsetHeight >
                scroll.offsetHeight + scroll.scrollTop
            ) {
                // list moving down
                element.scrollIntoView(false)
            } else if (element.offsetTop < scroll.scrollTop) {
                // list moving up
                element.scrollIntoView(true)
            } else if (this.currentSelected === '0.0') {
                scroll.scrollTop = 0
            }
        },
        focus() {
            this.$refs.input.$el.querySelector('input').focus()
        }
    },
    mounted() {
        if (this.autoFocus) this.focus()
    }
}
</script>

<style scoped>
.vs-input.active ::v-deep input {
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2) !important;
}

.auto-suggest-suggestions-list {
    overflow-y: auto !important;
    max-height: 40vh !important;
    z-index: 1000 !important;
}

.negate-validation-padding {
    margin-top: calc(0.5rem - 19px) !important;
}
</style>

<style lang="scss">
@import '@/assets/scss/vuexy/components/vxAutoSuggest.scss';
</style>
